import { useQuery } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GET_POST_REPLY_DRAFTS_QUERY } from '../graphql/resolvers/replyToPost/queries/getPostReplyDrafts.gql';
import {
  DocumentStatus,
  type PostDraftInterface,
} from '../interfaces/post.interface';
import {
  REMOVE_REPLY_DRAFT,
  SET_REPLY_DRAFTS,
  SET_REPLY_DRAFTS_LOADING,
} from '../store/replyDrafts.store';

interface ReplyDraftsStoreState {
  replyDraftsStore: {
    drafts: PostDraftInterface[];
    activeDraftId: string | null;
    loading: boolean;
    feedPostId: string | undefined;
    isAddingReplyDraft: boolean;
    publishInProgress: boolean;
    publishSuccess: boolean;
  };
}

export function useReplyDrafts(userId: string | undefined) {
  const dispatch = useDispatch();

  const { drafts, loading: storeLoading } = useSelector(
    (state: ReplyDraftsStoreState) => state.replyDraftsStore,
  );

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery(GET_POST_REPLY_DRAFTS_QUERY, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'network-only',
    onError: (error) => {
      // Server returned error — keep existing Redux state (don't clear).
      // Clearing on error would hide real drafts during transient outages.
      console.error('getPostReplyDrafts query error:', error.message);
    },
  });

  // Sync query result to Redux
  useEffect(() => {
    if (data?.getPostReplyDrafts) {
      dispatch(SET_REPLY_DRAFTS(data.getPostReplyDrafts));
    }
  }, [data, dispatch]);

  useEffect(() => {
    dispatch(SET_REPLY_DRAFTS_LOADING(queryLoading));
  }, [queryLoading, dispatch]);

  const refetchDrafts = useCallback(async () => {
    try {
      const result = await refetch();
      if (result.data?.getPostReplyDrafts) {
        dispatch(SET_REPLY_DRAFTS(result.data.getPostReplyDrafts));
      } else {
        dispatch(SET_REPLY_DRAFTS([]));
      }
    } catch {
      // Refetch failed — keep existing Redux state, don't clear
    }
  }, [refetch, dispatch]);

  const removeDraft = useCallback(
    (draftId: string) => {
      dispatch(REMOVE_REPLY_DRAFT(draftId));
    },
    [dispatch],
  );

  const getDraftForPost = useCallback(
    (postId: string): PostDraftInterface | undefined => {
      return drafts.find(
        (d) =>
          d.parentPostId === postId && d.status !== DocumentStatus.PUBLISHED,
      );
    },
    [drafts],
  );

  return {
    drafts,
    // TRIL-197: treat !userId (auth still hydrating) as loading so the
    // detail page's not-found gate doesn't fire prematurely on direct URL
    // load or refresh. Apollo's `skip: !userId` means queryLoading is
    // false while the query is skipped, so we synthesize the loading
    // state here for callers.
    loading: !userId || storeLoading || queryLoading,
    refetchDrafts,
    removeDraft,
    getDraftForPost,
  };
}
